import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { PhoneIphone, Computer, Tablet } from '@mui/icons-material'
import 'react-quill-new/dist/quill.snow.css';

const SplashImagePreview = ({ splashImage }) => {
  const [viewMode, setViewMode] = useState('desktop')

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode)
    }
  }

  // Device frame dimensions
  const getDeviceFrame = () => {
    return {
      desktop: { width: '100%', maxWidth: '1000px', height: '600px' },
      tablet: { width: '640px', height: '480px' },
      mobile: { width: '375px', height: '667px' } // More iPhone-like proportions
    }[viewMode]
  }

  // Get proportional splash height for preview
  const getPreviewSplashHeight = () => {
    const frameHeight = getDeviceFrame().height
    const frameHeightNum = parseInt(frameHeight)
    
    const proportions = {
      small: { desktop: 0.4, tablet: 0.45, mobile: 0.4 },    // Reduced mobile small
      medium: { desktop: 0.6, tablet: 0.65, mobile: 0.5 },   
      large: { desktop: 0.8, tablet: 0.85, mobile: 0.65 }     // 70% for mobile large
    }
    
    const size = splashImage.size || 'large'
    const proportion = proportions[size][viewMode]
    
    // Only status bar adjustment for mobile now
    const mobileAdjustment = viewMode === 'mobile' ? 24 : 0 // Just status bar
  
    return Math.round((frameHeightNum - mobileAdjustment) * proportion)
  }

  const getFontSize = () => {
    if (viewMode === 'mobile') {
      // Use clamp strategy like your actual CSS: clamp(24px, 8vw, 48px)
      // For preview, we'll simulate this with smaller fixed values
      return 'clamp(14px, 4vw, 22px)' // Scaled down for preview frame
    }
    return viewMode === 'tablet' ? '20px' : '28px'
  }

  const getButtonSize = () => {
    return viewMode === 'mobile' 
      ? { width: '120px', height: '40px', fontSize: '11px' } // Slightly larger than before
      : { width: '160px', height: '50px', fontSize: '13px' }
  }

  // Device frame styles
  const deviceFrameStyle = {
    ...getDeviceFrame(),
    margin: '0 auto',
    border: viewMode === 'mobile' ? '6px solid #333' : '2px solid #ddd',
    borderRadius: viewMode === 'mobile' ? '20px' : '8px',
    overflow: 'hidden',
    backgroundColor: '#fff',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    position: 'relative'
  }

  // Splash section styles with proportional height
  const splashSectionStyle = {
    height: getPreviewSplashHeight(),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundImage: (splashImage.image_preview_url || splashImage.image) 
      ? `url(${splashImage.image_preview_url || splashImage.image})`
      : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    backgroundSize: 'cover',
    backgroundPosition: 'center top',
    backgroundRepeat: 'no-repeat',
    padding: viewMode === 'mobile' ? '20px 12px' : '30px 16px'
  }

  const overlayStyle = splashImage.showOverlay ? {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: splashImage.overlayColor || 'rgba(0,0,0,0.3)',
    opacity: 0.5,
    zIndex: 1
  } : {}

  const contentStyle = {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: '90%',
    textAlign: 'center',
    color: splashImage.text_color || 'black'
  }

  const headlineStyle = {
    fontSize: getFontSize(),
    lineHeight: 1.2,
    margin: '0 0 15px 0',
    fontWeight: 'bold'
  }

  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: {
      left: 'flex-start',
      center: 'center', 
      right: 'flex-end'
    }[splashImage.button_position || 'center'],
    marginTop: '15px'
  }

  const buttonStyle = {
    ...getButtonSize(),
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: `2px solid ${splashImage.text_color || 'black'}`,
    color: splashImage.text_color || 'black',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    transition: 'all 0.3s ease',
    borderRadius: '0'
  }

  const [isHovered, setIsHovered] = useState(false)

  const hoveredButtonStyle = {
    ...buttonStyle,
    backgroundColor: splashImage.text_color || 'black',
    color: splashImage.text_color === 'white' ? 'black' : 'white'
  }

  // Mock content below splash - proportional to remaining space
  const mockContentStyle = {
    padding: viewMode === 'mobile' ? '16px 0' : '20px',
    backgroundColor: '#fff',
    height: `${parseInt(getDeviceFrame().height) - getPreviewSplashHeight() - (viewMode === 'mobile' ? 24 : 0)}px`, // Subtract status bar height
    overflow: 'hidden'
  }

  // Simplified mock content without navigation
  const MockContent = () => (
    <div style={mockContentStyle}>
      {/* Mock product grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: viewMode === 'mobile' ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: viewMode === 'mobile' ? '12px' : '20px',
        marginBottom: '16px',
        padding: viewMode === 'mobile' ? '0 16px' : '0'
      }}>
        {[1,2,3,4].map(i => (
          <div key={i} style={{
            backgroundColor: '#f8f9fa',
            aspectRatio: '3/4',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: viewMode === 'mobile' ? '10px' : '12px',
            color: '#666',
            border: '1px solid #e0e0e0'
          }}>
            Product {i}
          </div>
        ))}
      </div>

      {/* Mock text content */}
      <div style={{ padding: viewMode === 'mobile' ? '0 16px' : '0' }}>
        <div style={{ 
          height: viewMode === 'mobile' ? '20px' : '24px', 
          backgroundColor: '#333', 
          borderRadius: '4px',
          marginBottom: '12px',
          width: '70%'
        }}></div>
        <div style={{ 
          height: viewMode === 'mobile' ? '12px' : '14px', 
          backgroundColor: '#e9ecef', 
          borderRadius: '2px',
          marginBottom: '6px',
          width: '100%'
        }}></div>
        <div style={{ 
          height: viewMode === 'mobile' ? '12px' : '14px', 
          backgroundColor: '#e9ecef', 
          borderRadius: '2px',
          marginBottom: '6px',
          width: '85%'
        }}></div>
        <div style={{ 
          height: viewMode === 'mobile' ? '12px' : '14px', 
          backgroundColor: '#e9ecef', 
          borderRadius: '2px',
          width: '60%'
        }}></div>
      </div>
    </div>
  )

  return (
    <Box>
      {/* View Mode Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
        >
          <ToggleButton value="desktop">
            <Computer sx={{ mr: 1 }} />
            Desktop
          </ToggleButton>
          <ToggleButton value="tablet">
            <Tablet sx={{ mr: 1 }} />
            Tablet
          </ToggleButton>
          <ToggleButton value="mobile">
            <PhoneIphone sx={{ mr: 1 }} />
            Mobile
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Size Info - simplified */}
      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 2, color: 'text.secondary' }}>
        {splashImage.size || 'large'} size - {viewMode} preview
      </Typography>

      {/* Device Preview */}
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <div style={deviceFrameStyle}>
          {/* Status bar for mobile */}
          {viewMode === 'mobile' && (
            <div style={{
              height: '24px',
              backgroundColor: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
              fontSize: '12px',
              color: '#fff',
              fontWeight: '600'
            }}>
              <span>9:41</span>
              <div style={{ display: 'flex', gap: '2px' }}>
                <span style={{ fontSize: '10px' }}>●●●●●</span>
                <span style={{ marginLeft: '4px' }}>100%</span>
              </div>
            </div>
          )}

          {/* Splash Section */}
          <div style={splashSectionStyle}>
            {splashImage.showOverlay && <div style={overlayStyle} />}
            
            <div style={contentStyle}>
              {splashImage.headline_text && (
                <div 
                  style={headlineStyle}
                  dangerouslySetInnerHTML={{ __html: splashImage.headline_text }} 
                />
              )}
              
              {splashImage.button_text && (
                <div style={buttonContainerStyle}>
                  <button
                    style={isHovered ? hoveredButtonStyle : buttonStyle}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onClick={(e) => e.preventDefault()}
                  >
                    {splashImage.button_text}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mock content below */}
          <MockContent />
        </div>
      </Box>
    </Box>
  )
}

SplashImagePreview.propTypes = {
  splashImage: PropTypes.object.isRequired
}

export default SplashImagePreview
